import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Package, RefreshCw, Truck, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchDeliveryChallans } from '@/app/services/deliveryChallanService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';

interface StatusCardProps {
    label: string;
    count: number;
    subtitle: string;
    borderColor: string;
    iconBgColor: string;
    icon: React.ReactNode;
}

function StatusCard({ label, count, subtitle, borderColor, iconBgColor, icon }: StatusCardProps) {
    return (
        <div className={cn(
            "mobile-card border-l-4 active:scale-[0.98] transition-all",
            borderColor
        )}>
            <div className="flex items-center gap-3">
                <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
                    iconBgColor
                )}>
                    {icon}
                </div>
                <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
                    <p className="text-2xl font-bold mt-0.5">{count}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                </div>
            </div>
        </div>
    );
}

function DashboardSkeleton() {
    return (
        <div className="p-4 space-y-4">
            <div className="space-y-1">
                <MobileSkeleton className="h-7 w-40" />
                <MobileSkeleton className="h-5 w-56" />
            </div>
            <div className="mobile-card">
                <div className="flex items-center justify-between">
                    <div className="space-y-2">
                        <MobileSkeleton className="h-4 w-24" />
                        <MobileSkeleton className="h-10 w-16" />
                        <MobileSkeleton className="h-4 w-28" />
                    </div>
                    <MobileSkeleton className="w-14 h-14 rounded-xl" />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="mobile-card">
                        <div className="flex items-center gap-3">
                            <MobileSkeleton className="w-9 h-9 rounded-full" />
                            <div className="space-y-2">
                                <MobileSkeleton className="h-3 w-14" />
                                <MobileSkeleton className="h-7 w-8" />
                                <MobileSkeleton className="h-3 w-20" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <MobileSkeleton className="h-16 w-full rounded-xl" />
        </div>
    );
}

export default function DashboardPage() {
    const user = useAuthStore((state) => state.user);
    const username = user?.username ?? '';

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['delivery-challans-dashboard'],
        queryFn: () => fetchDeliveryChallans({ limit: 200 }),
        refetchInterval: 60000,
    });

    const statusSummary = useMemo(() => {
        const base = {
            packed: 0,
            shipped: 0,
            delivered: 0,
            cancelled: 0,
        } as Record<'packed' | 'shipped' | 'delivered' | 'cancelled', number>;

        if (data?.items) {
            data.items.forEach((challan) => {
                if (challan.status in base) {
                    base[challan.status as keyof typeof base] += 1;
                }
            });
        }

        return base;
    }, [data]);

    const total = data?.total ?? 0;

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="Dashboard"
                rightAction={
                    <button
                        onClick={() => refetch()}
                        className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                        disabled={isFetching}
                    >
                        <RefreshCw
                            className={cn(
                                "h-5 w-5 text-muted-foreground",
                                isFetching && "animate-spin"
                            )}
                        />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {isLoading ? (
                    <DashboardSkeleton />
                ) : isError ? (
                    <div className="p-4">
                        <div className="mobile-card border-l-4 border-l-red-500">
                            <p className="text-sm font-semibold text-red-500">
                                Failed to load dashboard
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {error instanceof Error ? error.message : 'An unexpected error occurred'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {/* Greeting */}
                        <div>
                            <h2 className="text-xl font-bold">
                                Hello, {username || 'Agent'}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                Here's your delivery overview
                            </p>
                        </div>

                        {/* Total Assigned Card */}
                        <div className="mobile-card bg-primary/5 border border-primary/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                                        Total Assigned
                                    </p>
                                    <p className="text-4xl font-bold mt-1">
                                        {total}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Delivery challans
                                    </p>
                                </div>
                                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                    <Package className="h-7 w-7 text-primary" />
                                </div>
                            </div>
                        </div>

                        {/* Status Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <StatusCard
                                label="Packed"
                                count={statusSummary.packed}
                                subtitle="Ready for pickup"
                                borderColor="border-l-green-500"
                                iconBgColor="bg-green-500/10 text-green-500"
                                icon={<Package className="h-4 w-4" />}
                            />
                            <StatusCard
                                label="In Transit"
                                count={statusSummary.shipped}
                                subtitle="On the way"
                                borderColor="border-l-blue-500"
                                iconBgColor="bg-blue-500/10 text-blue-500"
                                icon={<Truck className="h-4 w-4" />}
                            />
                            <StatusCard
                                label="Delivered"
                                count={statusSummary.delivered}
                                subtitle="Successfully completed"
                                borderColor="border-l-amber-500"
                                iconBgColor="bg-amber-500/10 text-amber-500"
                                icon={<CheckCircle className="h-4 w-4" />}
                            />
                            <StatusCard
                                label="Cancelled"
                                count={statusSummary.cancelled}
                                subtitle="Not completed"
                                borderColor="border-l-red-500"
                                iconBgColor="bg-red-500/10 text-red-500"
                                icon={<XCircle className="h-4 w-4" />}
                            />
                        </div>

                        {/* View All Action Card */}
                        <Link to="/deliveries" className="block">
                            <div className="mobile-card active:scale-[0.98] transition-all bg-primary/5 border border-primary/20">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-base font-semibold">
                                            View All Delivery Challans
                                        </p>
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                            Manage and update delivery status
                                        </p>
                                    </div>
                                    <ChevronRight className="h-5 w-5 text-primary shrink-0" />
                                </div>
                            </div>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
